import React, { useState, useEffect, useCallback } from 'react';
import { History, Loader2, ChevronLeft, ChevronRight, Filter, AlertCircle } from 'lucide-react';
import Modal from './ui/Modal';
import Button from './ui/Button';
import DatePicker from './ui/DatePicker';
import { formatDateTime } from '../utils/formatDate';
import { API_PATH, request } from '../config/api';
import styles from './CoinHistoryModal.module.css';

const PAGE_SIZE_OPTIONS = [10, 20, 50];
const SORT_OPTIONS = [
    { value: 'created_at_desc', sortBy: 'created_at', sortOrder: 'desc', label: 'Date (newest first)' },
    { value: 'created_at_asc', sortBy: 'created_at', sortOrder: 'asc', label: 'Date (oldest first)' },
    { value: 'amount_desc', sortBy: 'amount', sortOrder: 'desc', label: 'Amount (high to low)' },
    { value: 'amount_asc', sortBy: 'amount', sortOrder: 'asc', label: 'Amount (low to high)' },
];
const TYPE_OPTIONS = [
    { value: '', label: 'All types' },
    { value: 'redeem', label: 'Redeem' },
    { value: 'poll_create', label: 'Poll created' },
    { value: 'purchase', label: 'Coin purchase' },
];

function getDefaultDateRange() {
    const to = new Date();
    const from = new Date(to);
    from.setMonth(from.getMonth() - 1);
    return {
        from: from.toISOString().slice(0, 10),
        to: to.toISOString().slice(0, 10),
    };
}

export default function CoinHistoryModal({ isOpen, onClose, onRefresh }) {
    const defaultRange = getDefaultDateRange();
    const [from, setFrom] = useState(defaultRange.from);
    const [to, setTo] = useState(defaultRange.to);
    const [typeFilter, setTypeFilter] = useState('');
    const [sortValue, setSortValue] = useState('created_at_desc');
    const [pageSize, setPageSize] = useState(20);
    const [page, setPage] = useState(1);

    const [data, setData] = useState({ transactions: [], pagination: null, filter: null });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchHistory = useCallback(async () => {
        if (!isOpen) return;
        setLoading(true);
        setError(null);
        try {
            const sortOpt = SORT_OPTIONS.find((o) => o.value === sortValue) || SORT_OPTIONS[0];
            const params = new URLSearchParams({
                page: String(page),
                pageSize: String(pageSize),
                from,
                to,
                sortBy: sortOpt.sortBy,
                sortOrder: sortOpt.sortOrder,
            });
            if (typeFilter) params.set('type', typeFilter);
            const res = await request(`${API_PATH}/auth/coin-history?${params.toString()}`);
            const json = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(json.error || 'Failed to load history');
                setData({ transactions: [], pagination: null, filter: null });
                return;
            }
            setData({
                transactions: json.transactions || [],
                pagination: json.pagination || { page: 1, totalPages: 1, total: 0 },
                filter: json.filter,
            });
            onRefresh?.();
        } catch (e) {
            setError('Network error');
            setData({ transactions: [], pagination: null, filter: null });
        } finally {
            setLoading(false);
        }
    }, [isOpen, page, pageSize, from, to, sortValue, typeFilter, onRefresh]);

    useEffect(() => {
        if (isOpen) {
            setPage(1);
            setFrom(defaultRange.from);
            setTo(defaultRange.to);
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) fetchHistory();
    }, [isOpen, fetchHistory]);

    const handleApplyFilter = () => {
        setPage(1);
    };

    const sortOpt = SORT_OPTIONS.find((o) => o.value === sortValue) || SORT_OPTIONS[0];
    const { transactions, pagination } = data;
    const totalPages = pagination?.totalPages ?? 1;
    const total = pagination?.total ?? 0;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Coin history"
            size="xl"
            className={styles.modal}
        >
            <div className={styles.container}>
                <div className={styles.filters} role="region" aria-label="Filters">
                    <div className={styles.filterRow}>
                        <label className={styles.filterLabel}>
                            <Filter size={14} aria-hidden /> Date range
                        </label>
                        <DatePicker value={from} onChange={setFrom} max={to} />
                        <span className={styles.filterTo}>to</span>
                        <DatePicker value={to} onChange={setTo} min={from} />
                    </div>
                    <div className={styles.filterRow}>
                        <label className={styles.filterLabel}>Type</label>
                        <select
                            className={styles.select}
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                            aria-label="Filter by type"
                        >
                            {TYPE_OPTIONS.map((opt) => (
                                <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.filterRow}>
                        <label className={styles.filterLabel}>Sort by</label>
                        <select
                            className={styles.select}
                            value={sortValue}
                            onChange={(e) => setSortValue(e.target.value)}
                            aria-label="Sort by"
                        >
                            {SORT_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.filterRow}>
                        <label className={styles.filterLabel}>Per page</label>
                        <select
                            className={styles.select}
                            value={pageSize}
                            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                            aria-label="Rows per page"
                        >
                            {PAGE_SIZE_OPTIONS.map((n) => (
                                <option key={n} value={n}>{n}</option>
                            ))}
                        </select>
                    </div>
                    <Button variant="primary" size="sm" className={styles.applyBtn} onClick={handleApplyFilter} disabled={loading}>
                        Apply
                    </Button>
                </div>

                {error && (
                    <div className={styles.errorBanner} role="alert">
                        <AlertCircle size={16} aria-hidden />
                        <span>{error}</span>
                    </div>
                )}

                {loading ? (
                    <div className={styles.loading}>
                        <Loader2 size={28} className={styles.spinner} aria-hidden />
                        <span>Loading…</span>
                    </div>
                ) : (
                    <>
                        <div className={styles.tableWrap}>
                            <table className={styles.table} role="table">
                                <thead>
                                    <tr>
                                        <th scope="col">Date</th>
                                        <th scope="col">Description</th>
                                        <th scope="col" className={styles.amountCol}>Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transactions.length === 0 ? (
                                        <tr>
                                            <td colSpan={3} className={styles.emptyCell}>
                                                <div className={styles.emptyState}>
                                                    <History size={28} className={styles.emptyIcon} aria-hidden />
                                                    <span>No transactions in this date range. Try a different range or filter.</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        transactions.map((t) => (
                                            <tr key={t.id}>
                                                <td className={styles.dateCell}>{formatDateTime(t.created_at) || '—'}</td>
                                                <td>{t.description || (t.type === 'redeem' ? 'Coupon redeemed' : 'Poll created')}</td>
                                                <td className={`${styles.amountCell} ${t.amount >= 0 ? styles.credit : styles.debit}`}>
                                                    {t.amount >= 0 ? '+' : ''}{t.amount}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {total > 0 && (
                            <div className={styles.pagination} role="navigation" aria-label="Pagination">
                                <span className={styles.paginationSummary}>
                                    Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
                                </span>
                                <div className={styles.paginationButtons}>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        disabled={page <= 1 || loading}
                                        icon={<ChevronLeft size={16} />}
                                        aria-label="Previous page"
                                    >
                                        Previous
                                    </Button>
                                    <span className={styles.pageIndicator} aria-live="polite">
                                        Page {page} of {totalPages}
                                    </span>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                        disabled={page >= totalPages || loading}
                                        icon={<ChevronRight size={16} />}
                                        iconRight={<ChevronRight size={16} />}
                                        aria-label="Next page"
                                    >
                                        Next
                                    </Button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
}
